import type { Identifier } from "ra-core";

/**
 * The call a setter just started from the phone. The CRM can't see WhatsApp
 * Business calls, so it remembers who they went to call and, when they come
 * back to the CRM, asks how it went.
 */
export interface PendingCall {
  clientId: string;
  name: string;
  /** When they tapped "Llamar" (ms) */
  at: number;
}

const KEY = "gula-pending-call";
export const PENDING_CALL_EVENT = "gula-pending-call";
/** After this the question no longer makes sense */
export const PENDING_MAX_AGE_MS = 2 * 60 * 60 * 1000;
/** Coming back sooner than this means they never really left to call */
export const MIN_AWAY_MS = 3000;

export const savePendingCall = (clientId: Identifier, name: string) => {
  const value: PendingCall = {
    clientId: String(clientId),
    name,
    at: Date.now(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // private window: nothing is remembered
  }
  window.dispatchEvent(new Event(PENDING_CALL_EVENT));
};

export const readPendingCall = (now = Date.now()): PendingCall | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingCall;
    if (!value?.clientId || typeof value.at !== "number") return null;
    if (now - value.at > PENDING_MAX_AGE_MS) return null;
    return value;
  } catch {
    return null;
  }
};

export const clearPendingCall = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
};

/** Time to ask "¿Cómo ha ido?": a recent call and enough time away. */
export const shouldAskResult = (
  pending: PendingCall | null,
  now = Date.now(),
): pending is PendingCall =>
  !!pending &&
  now - pending.at >= MIN_AWAY_MS &&
  now - pending.at <= PENDING_MAX_AGE_MS;

/** Digits typed in the "Me llaman" search, ready to look up a phone. */
export const searchDigits = (text: string) => text.replace(/\D/g, "");

/** "hace 5 min", "hace 2 h", "ayer"… for the "Me llaman" list. */
export const agoLabel = (date?: string | null, now = Date.now()) => {
  if (!date) return "";
  const minutes = Math.max(0, Math.round((now - Date.parse(date)) / 60_000));
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return hours < 48 ? "ayer" : `hace ${Math.round(hours / 24)} días`;
};
